/** Homepage card-section empty states: copy, illustrations, and My Work selection. */
import type {ComponentType, SVGAttributes} from 'react';

import {MyWorkCategory} from './my-work/MyWorkConstants';
import jumpBackInIllustration from './svgs/jump_back_in.svg';
import noEntriesIllustration from './svgs/no_entries.svg';
import noResultsIllustration from './svgs/no_results.svg';
import noTasksIllustration from './svgs/no_tasks.svg';
import noWorklistsIllustration from './svgs/no_worklists.svg';
import noWorksheetsIllustration from './svgs/no_worksheets.svg';
import runningNowIllustration from './svgs/running_now.svg';

export interface HomepageEmptyState {
  title: string;
  subtext?: string;
  illustration: ComponentType<SVGAttributes<SVGSVGElement>>;
}

/** Empty states for homepage card sections outside My Work. */
export const HOMEPAGE_SECTION_EMPTY_STATES: Record<string, HomepageEmptyState> = {
  jumpBackIn: {
    title: 'No recently opened items',
    illustration: jumpBackInIllustration,
  },
  runningNow: {
    title: 'No processes are currently running',
    illustration: runningNowIllustration,
  },
  worklists: {
    title: 'No worklists yet',
    subtext: 'Create one to start organizing your work',
    illustration: noWorklistsIllustration,
  },
} as const;

const MY_WORK_FILTERED_EMPTY_STATE: HomepageEmptyState = {
  title: 'No results match your filters',
  illustration: noResultsIllustration,
};

const MY_WORK_CATEGORY_EMPTY_STATES: Record<MyWorkCategory, HomepageEmptyState> = {
  [MyWorkCategory.ENTRIES]: {
    title: 'No entries yet',
    subtext: 'Entries you create will appear here',
    illustration: noEntriesIllustration,
  },
  [MyWorkCategory.WORKSHEETS]: {
    title: 'No worksheets yet',
    subtext: 'Worksheets you create will appear here',
    illustration: noWorksheetsIllustration,
  },
  [MyWorkCategory.TASKS]: {
    title: "You're all caught up",
    subtext: 'No tasks are assigned to you',
    illustration: noTasksIllustration,
  },
};

/** My Work empty states keyed for tests and integration assertions. */
export const HOMEPAGE_MY_WORK_EMPTY_STATES = {
  filtered: MY_WORK_FILTERED_EMPTY_STATE,
  entries: MY_WORK_CATEGORY_EMPTY_STATES[MyWorkCategory.ENTRIES],
  worksheets: MY_WORK_CATEGORY_EMPTY_STATES[MyWorkCategory.WORKSHEETS],
  tasks: MY_WORK_CATEGORY_EMPTY_STATES[MyWorkCategory.TASKS],
} as const;

/** Picks the My Work empty state based on whether items exist at all. */
export function getMyWorkEmptyState(category: MyWorkCategory, totalCount: number): HomepageEmptyState {
  if (totalCount > 0) {
    return MY_WORK_FILTERED_EMPTY_STATE;
  }
  return MY_WORK_CATEGORY_EMPTY_STATES[category];
}
